import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function AdminLogin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO({ title: "Admin Login — Mashara Finance", description: "Administrator sign in.", path: "/admin/login", noindex: true });

  useEffect(() => {
    if (user?.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      if (u.role !== "admin") {
        await logout();
        setError("This account does not have admin access.");
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-emerald-700 text-white flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></span>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Admin Login</h1>
            <p className="text-sm text-slate-500">Mashara Finance website control panel</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="admin-email">Admin email</Label>
            <Input id="admin-email" data-testid="admin-login-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@company.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" data-testid="admin-login-password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <Button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-6 font-semibold">
            {loading ? "Signing in…" : "Sign in to Admin"}
          </Button>
        </form>
        <p className="text-xs text-slate-400 text-center mt-6">Authorized administrators only. Customer login is at <a href="/login" className="text-emerald-700 font-medium">/login</a>.</p>
      </div>
    </div>
  );
}
